/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useState } from 'react';
import ToolbarDropdown from './ToolbarDropdown';
import { CLIENT_TYPE } from '@/components/forms/CreateNewBusinessForm/validationSchema';
import {
	EMAIL_TEMPLATE_TYPE_KEYS,
	EMAIL_TEMPLATE_TYPES,
	FIELDS,
	removeWhitespaceFromHtml,
} from '@/utils/emailHelper';
import {
	useGetPreRenewalEmailTemplateDataQuery,
	useGetSOAEmailTemplateDataQuery,
	useLazyGetEmailTemplateBlockByTypeQuery,
	useLazyGetPreRenewalEmailTemplateDataQuery,
	useLazyGetSOAEmailTemplateDataQuery,
} from '@/features/templates/templatesApi';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { formatDataForScopeOfAdvice } from '@/features/templates/helper';
import { useGetDealRowQuery } from '@/features/deals/dealsApi';
import { useGetClientPropertiesQuery } from '@/features/clients/clientsApi';
import { useGetLatestDealSOAsQuery } from '@/features/factFind/factFindApi';
import Handlebars from '@/utils/handlebars';
import { useGetClassesOfRiskQuery } from '@/features/classesOfRisk/classesOfRiskApi';
import { useFeatureFlagContext } from '@/context/featureFlagContext';

const PresetTableDropdown = ({ dealId, clientId, onChange, templateType }) => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const params = { dealId };

	const countryCode = userDetails?.broker?.country ?? 'NZ';
	const brokerGroupIds = userDetails?.brokerGroupArray;

	const { getFeatureFlags } = useFeatureFlagContext();
	const marbleLedger = Boolean(getFeatureFlags('marbleSOA')?.active);

	const { data: dealRow } = useGetDealRowQuery(dealId, {
		skip: !dealId,
	});

	const { data: classesOfRisk } = useGetClassesOfRiskQuery(
		{ countryCode, clientType: dealRow?.[CLIENT_TYPE], brokerGroupIds },
		{ skip: userDetails.isLoading || userDetails.isUninitialized },
	);

	const { data: clientData } = useGetClientPropertiesQuery(clientId, {
		skip: !clientId,
	});

	const soaData = useGetLatestDealSOAsQuery({ dealId }, { skip: !dealId });
	const soas = soaData.data ?? [];

	const [getBlockType] = useLazyGetEmailTemplateBlockByTypeQuery();

	const canSelectStatementOfAdvice =
		templateType === EMAIL_TEMPLATE_TYPE_KEYS.statementOfAdvice || soas.length > 0;

	const preRenewalQuery = useGetPreRenewalEmailTemplateDataQuery(params, {
		skip: !dealId || !userDetails,
	});
	const soaQuery = useGetSOAEmailTemplateDataQuery(params, {
		skip: !dealId || !userDetails || !canSelectStatementOfAdvice,
	});

	const [fields, setFields] = useState([]);

	useEffect(() => {
		const getFieldData = async () => {
			for (const field of fieldData) {
				const block = await getBlockType({ type: `${field.type}.${field.value}` }).unwrap();
				field.html = block.html;
			}
			setFields(fieldData);
		};
		const fieldData = FIELDS.filter((field) => field.block).map((field) => {
			const disabled =
				field.requirements === EMAIL_TEMPLATE_TYPE_KEYS.statementOfAdvice
					? !canSelectStatementOfAdvice
					: false;
			return {
				...field,
				disabled,
				disabledText: disabled
					? dealId === '[TEST_DEAL]'
						? `Template type should be ${EMAIL_TEMPLATE_TYPES.statementOfAdvice}`
						: 'Deal does not have a generated Statement of Advice'
					: '',
			};
		});
		getFieldData();
	}, [canSelectStatementOfAdvice]);

	const getTemplateData = (type) => {
		switch (type) {
			case EMAIL_TEMPLATE_TYPE_KEYS.scopeOfAdvice:
				return formatDataForScopeOfAdvice({
					client: clientData,
					dealRow,
					userData: userDetails,
					isMarble: marbleLedger,
					classesOfRisk,
				});
			case EMAIL_TEMPLATE_TYPE_KEYS.statementOfAdvice: {
				const data = soaQuery.data;
				return data?.data;
			}

			case EMAIL_TEMPLATE_TYPE_KEYS.preRenewal: {
				const data = preRenewalQuery.data;
				return data?.data;
			}
			default:
				return {};
		}
	};

	const getNodeValue = async (event) => {
		const field = fields.find((v) => v.value === event.target.value);

		try {
			const template = Handlebars.compile(
				field.html.toString().replace(/>\s+</g, '><').replace(/\n/g, ''),
			);

			const data = getTemplateData(field.requirements);
			const html = removeWhitespaceFromHtml(
				template({
					...data,
				}),
			);
			onChange(html);
		} catch (e) {
			console.error('Error getting html', field, event);
		}
	};

	return <ToolbarDropdown title={'pre-set tables'} fields={fields} onChange={getNodeValue} />;
};

export default PresetTableDropdown;
